import { useState } from 'react';
import { useBooleanFlagValue } from '@openfeature/react-sdk';
import { useQuery } from '@tanstack/react-query';
import {
  BookOpenIcon,
  Database,
  FileText,
  ImagesIcon,
  LayoutTemplate,
  Palette,
  Radio,
  RecycleIcon,
  RefreshCwIcon,
  ShieldCheck,
  Tags,
  Users,
} from 'lucide-react';
import {
  brandMediaQueryOptions,
  mediaFacetsQueryOptions,
  totalItemsInScope,
} from '@/entities/brand-media';
import { mediaConnectorsQueryOptions } from '@/entities/media-connector';
import { isEnforced, mediaGuidelinesQueryOptions } from '@/entities/media-guideline';
import { briefTemplatesQueryOptions } from '@/entities/brief-template';
import { taxonomiesQueryOptions } from '@/entities/taxonomy';
import { usePersonas } from '@/entities/persona';
import { usePermissions } from '@/entities/user';
import { useCurrentWorkspaceId } from '@/entities/workspace';
import { useFolders } from '@/features/guidelines';
import { useKnowledgeBasesList } from '@/features/knowledge-bases';
import { useEmailTemplates } from '@/features/templates-config';
import { StaggerItem } from '@/shared/ui/animations';
import {
  ListPageLayout,
  PageHeader,
  PageHeaderIdentity,
  PageHeaderTitle,
} from '@/shared/ui/layout';
import { AboutPageDialog } from '@/shared/ui';
import { ROUTES } from '@/shared/config/routes';
import { KnowledgeHubCard, KnowledgeHubCardSkeleton } from './components/KnowledgeHubCard';
import { useTranslation } from 'react-i18next';

/**
 * The Knowledge landing grid: one tile per top-level destination.
 *
 * Each card has its OWN visibility predicate driven by `usePermissions()`, and
 * some AND-gate it against an OpenFeature flag. Because the per-card data
 * hooks are heavy, every gated card is extracted to its own component and
 * mounts only when its gate passes, so the query never runs for a card the
 * user cannot see.
 *
 * The grid is the page canvas rather than a container: the cards supply their
 * own surface, so the 8px grid step is the only thing between them.
 */
export const KnowledgeHub = () => {
  const { t } = useTranslation('knowledge');
  const [isAboutOpen, setIsAboutOpen] = useState(false);
  const projectsModuleEnabled = useBooleanFlagValue('projects_module', false);
  const taxonomyEnabled = useBooleanFlagValue('knowledge_hub_taxonomy', false);
  const knowledgeMediaEnabled = useBooleanFlagValue('knowledge_media', false);
  const mediaGuidelinesEnabled = useBooleanFlagValue('media_guidelines', false);
  const contentStudioEnabled = useBooleanFlagValue('content_studio_mvp', false);

  const {
    canViewAnyKnowledgeBase,
    canViewBriefTemplates,
    canViewAudiences,
    canViewBrandVisualGuidelines,
    canOpenBrandAssets,
    canManageEmailTemplates,
    canViewTaxonomy,
  } = usePermissions();

  const showBriefTemplatesCard = canViewBriefTemplates && projectsModuleEnabled;
  const showTaxonomyCard = canViewTaxonomy && taxonomyEnabled;

  return (
    <ListPageLayout>
      {/* Staggered entrance: the header leads and the cards cascade from the
          shared content slot (index 3) onward, on the same rhythm every page
          uses. */}
      <StaggerItem index={0}>
        <PageHeader>
          <PageHeaderIdentity>
            <PageHeaderTitle info={{ onClick: () => setIsAboutOpen(true) }}>
              {t('knowledgeHub.knowledgeHub.knowledge')}
            </PageHeaderTitle>
          </PageHeaderIdentity>
        </PageHeader>
      </StaggerItem>

      <div className="min-h-0 flex-1 overflow-y-auto">
        <div
          data-testid="knowledge-hub-card-grid"
          className="grid grid-cols-1 gap-grid sm:grid-cols-2 lg:grid-cols-3"
        >
          {canViewAnyKnowledgeBase && (
            <StaggerItem index={3}>
              <KnowledgeBasesCard />
            </StaggerItem>
          )}
          {canViewAudiences && (
            <StaggerItem index={4}>
              <AudiencesCard />
            </StaggerItem>
          )}
          {canViewBrandVisualGuidelines && (
            <StaggerItem index={5}>
              <GuidelinesCard />
            </StaggerItem>
          )}
          {canManageEmailTemplates && (
            <StaggerItem index={6}>
              <TemplatesCard />
            </StaggerItem>
          )}
          {showBriefTemplatesCard && (
            <StaggerItem index={7}>
              <BriefTemplatesCard />
            </StaggerItem>
          )}
          {showTaxonomyCard && (
            <StaggerItem index={8}>
              <TaxonomyCard />
            </StaggerItem>
          )}
          {knowledgeMediaEnabled && (
            <StaggerItem index={9}>
              <MediaCard />
            </StaggerItem>
          )}
          {mediaGuidelinesEnabled && (
            <StaggerItem index={10}>
              <MediaGuidelinesCard />
            </StaggerItem>
          )}
          {contentStudioEnabled && canOpenBrandAssets && (
            <StaggerItem index={11}>
              <BrandMediaCard />
            </StaggerItem>
          )}
        </div>
      </div>

      <AboutPageDialog
        open={isAboutOpen}
        onOpenChange={setIsAboutOpen}
        title={t('knowledgeHub.knowledgeHub.about.title')}
        description={t('knowledgeHub.knowledgeHub.about.description')}
        rows={[
          { icon: BookOpenIcon, text: t('knowledgeHub.knowledgeHub.about.groundAgents') },
          { icon: RecycleIcon, text: t('knowledgeHub.knowledgeHub.about.reuseContext') },
          { icon: RefreshCwIcon, text: t('knowledgeHub.knowledgeHub.about.singleSource') },
        ]}
      />
    </ListPageLayout>
  );
};

KnowledgeHub.displayName = 'KnowledgeHub';

/**
 * Pairs a count with its pluralised noun, or supplies neither.
 *
 * `undefined` is a request that never landed. None of these queries retry, none
 * throw to a boundary, and none render an error of their own, so a failed one
 * just clears its skeleton with nothing behind it. Flattening that to zero would
 * have the tile state an empty section, the one reading the badge exists to make
 * actionable, for inventory the app never obtained.
 */
function countBadge(count: number | undefined, label: (count: number) => string) {
  return count === undefined ? {} : { count, countLabel: label(count) };
}

/**
 * Knowledge Files (knowledge bases) card. Mounted only when
 * `canViewAnyKnowledgeBase` is true so the underlying query is gated.
 */
function KnowledgeBasesCard() {
  const { t } = useTranslation('knowledge');

  const { data, isPending } = useKnowledgeBasesList(undefined, true);

  if (isPending) return <KnowledgeHubCardSkeleton />;

  return (
    <KnowledgeHubCard
      icon={Database}
      tint="purple"
      title={t('knowledgeHub.knowledgeHub.knowledgeFiles')}
      description={t('knowledgeHub.knowledgeHub.knowledgeFilesDescription')}
      to={ROUTES.KNOWLEDGE_HUB_KB}
      {...countBadge(data?.length, (count) =>
        t('knowledgeHub.knowledgeHub.collectionCount', { count }),
      )}
    />
  );
}

/**
 * Audiences (personas) card. Mounted only when `canViewAudiences` is true.
 */
function AudiencesCard() {
  const { t } = useTranslation('knowledge');

  const workspaceId = useCurrentWorkspaceId();
  const { data, isPending } = usePersonas(workspaceId, true);

  if (isPending) return <KnowledgeHubCardSkeleton />;

  return (
    <KnowledgeHubCard
      icon={Users}
      tint="blue"
      title={t('knowledgeHub.knowledgeHub.audiences')}
      description={t('knowledgeHub.knowledgeHub.audiencesDescription')}
      to={ROUTES.KNOWLEDGE_HUB_AUDIENCES}
      {...countBadge(data?.length, (count) =>
        t('knowledgeHub.knowledgeHub.audienceCount', { count }),
      )}
    />
  );
}

/**
 * Brand & Visual guidelines card. Mounted only when
 * `canViewBrandVisualGuidelines` is true.
 */
function GuidelinesCard() {
  const { t } = useTranslation('knowledge');

  const { data, isPending } = useFolders(true);

  if (isPending) return <KnowledgeHubCardSkeleton />;

  return (
    <KnowledgeHubCard
      icon={Palette}
      tint="pink"
      title={t('knowledgeHub.knowledgeHub.brandVisualGuidelines')}
      description={t('knowledgeHub.knowledgeHub.brandVisualGuidelinesDescription')}
      to={ROUTES.KNOWLEDGE_HUB_VISUAL_GUIDELINES}
      {...countBadge(data?.length, (count) =>
        t('knowledgeHub.knowledgeHub.folderCount', { count }),
      )}
    />
  );
}

function TemplatesCard() {
  const { t } = useTranslation('knowledge');

  // `isError` rather than the list itself, because the hook flattens `data ?? []`:
  // an empty array here is indistinguishable from a request that failed.
  const { templates, isLoading, isError } = useEmailTemplates();

  if (isLoading) return <KnowledgeHubCardSkeleton />;

  return (
    <KnowledgeHubCard
      icon={LayoutTemplate}
      tint="orange"
      title={t('knowledgeHub.knowledgeHub.emailTemplates')}
      description={t('knowledgeHub.knowledgeHub.emailTemplatesDescription')}
      to={ROUTES.KNOWLEDGE_HUB_TEMPLATES}
      {...countBadge(isError ? undefined : templates.length, (count) =>
        t('knowledgeHub.knowledgeHub.templateCount', { count }),
      )}
    />
  );
}

/**
 * Brief Templates card. Mounted only when BOTH
 * `canViewAnyKnowledgeBase` and the `projects_module` OpenFeature flag
 * are on (the AND-gate enforced by the parent). Same flag convention
 * used by the `/projects` route — Projects has no permission system of
 * its own and gates only on `projects_module`.
 */
function BriefTemplatesCard() {
  const { t } = useTranslation('knowledge');

  const { data, isPending } = useQuery(briefTemplatesQueryOptions());

  if (isPending) return <KnowledgeHubCardSkeleton />;

  return (
    <KnowledgeHubCard
      icon={FileText}
      tint="lime"
      title={t('knowledgeHub.knowledgeHub.briefTemplates')}
      description={t('knowledgeHub.knowledgeHub.briefTemplatesDescription')}
      to={ROUTES.KNOWLEDGE_HUB_BRIEF_TEMPLATES}
      {...countBadge(data?.length, (count) =>
        t('knowledgeHub.knowledgeHub.templateCount', { count }),
      )}
    />
  );
}

/**
 * Taxonomy card. Mounted only when BOTH `canViewTaxonomy` and the
 * `knowledge_hub_taxonomy` OpenFeature flag are on (the AND-gate is enforced
 * by the parent). Links to the versioned naming-schema administration surface.
 */
function TaxonomyCard() {
  const { t } = useTranslation('knowledge');
  const { data, isPending } = useQuery(taxonomiesQueryOptions());

  if (isPending) return <KnowledgeHubCardSkeleton />;

  return (
    <KnowledgeHubCard
      icon={Tags}
      title={t('knowledgeHub.knowledgeHub.taxonomyAdministration')}
      description={t('taxonomy.hubCard.description')}
      to={ROUTES.KNOWLEDGE_HUB_TAXONOMY}
      {...countBadge(data?.length, (count) => t('taxonomy.hubCard.countLabel', { count }))}
    />
  );
}

/**
 * Media Guidelines: the house standards Delivery's pre-flight checks a plan
 * against. Counts only the guidelines actually being enforced, since a draft is
 * not checking anything.
 */
function MediaGuidelinesCard() {
  const { t } = useTranslation('knowledge');
  const { data, isPending } = useQuery(mediaGuidelinesQueryOptions({ pageSize: 100 }));

  if (isPending) return <KnowledgeHubCardSkeleton />;

  return (
    <KnowledgeHubCard
      icon={ShieldCheck}
      tint="teal"
      title={t('knowledgeHub.knowledgeHub.mediaGuidelines')}
      description={t('knowledgeHub.knowledgeHub.mediaGuidelinesDescription')}
      to={ROUTES.KNOWLEDGE_HUB_MEDIA_GUIDELINES}
      {...countBadge(data?.items.filter((guideline) => isEnforced(guideline)).length, (count) =>
        t('knowledgeHub.knowledgeHub.standardCount', { count }),
      )}
    />
  );
}

/**
 * The brand's own photography and footage.
 *
 * Gated on the permission as well as the flag because the route behind it is, so a card shown
 * without `canOpenBrandAssets` would be a tile that redirects you home rather than a
 * way in.
 */
function BrandMediaCard() {
  const { t } = useTranslation('knowledge');
  const { data, isPending: isRowsPending } = useQuery(brandMediaQueryOptions({}));
  const { data: facets, isPending: isFacetsPending } = useQuery(mediaFacetsQueryOptions({}));
  const isPending = isRowsPending || isFacetsPending;

  if (isPending) return <KnowledgeHubCardSkeleton />;

  return (
    <KnowledgeHubCard
      icon={ImagesIcon}
      tint="red"
      title={t('knowledgeHub.knowledgeHub.brandMedia')}
      description={t('knowledgeHub.knowledgeHub.brandMediaDescription')}
      to={ROUTES.KNOWLEDGE_HUB_BRAND_MEDIA}
      {...countBadge(
        // Both, because the total is the highest candidate across the two: either
        // one missing understates the scope rather than narrowing it.
        data && facets ? totalItemsInScope(facets, data.length) : undefined,
        (count) => t('knowledgeHub.knowledgeHub.itemCount', { count }),
      )}
    />
  );
}

function MediaCard() {
  const { t } = useTranslation('knowledge');
  const { data, isPending } = useQuery(mediaConnectorsQueryOptions());

  if (isPending) return <KnowledgeHubCardSkeleton />;

  return (
    <KnowledgeHubCard
      icon={Radio}
      tint="yellow"
      title={t('knowledgeHub.knowledgeHub.paidMediaAdIntelligence')}
      description={t('knowledgeHub.knowledgeHub.paidMediaDescription')}
      to={ROUTES.KNOWLEDGE_HUB_MEDIA}
      {...countBadge(data?.items.filter((c) => c.status === 'connected').length, (count) =>
        t('knowledgeHub.knowledgeHub.sourceCount', { count }),
      )}
    />
  );
}
